import { useState } from 'react';

import { Notice } from '@/components/Modal';
import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { nasDownloadUrl } from '@/pages/docs/useDocuments';

/**
 * 근거 문서 카드 — 답변이 무엇을 보고 말했는지 (docs/18 §14.2 · DOC-20 §3.1 4번).
 *
 * 에이전트는 자료실 검색 결과마다 `citation` 이벤트를 하나씩 낸다. 그 이벤트에는 <b>문서 id 와
 * 이름·조항</b>만 담긴다 — 내려받기 링크는 담기지 않는다.
 *
 * ## 왜 링크를 이벤트에 싣지 않는가
 *
 * presigned URL 은 <b>짧게 유효한 비밀</b>이다. 대화 이벤트에 실으면 그 값이 대화 이력·로그에
 * 남고, 만들어 두었다가 아무도 누르지 않으면 그냥 새어 나간 링크가 된다. 그래서 이벤트는 id 만
 * 나르고, 링크는 <b>사용자가 누른 그 순간</b> 백엔드가 만든다.
 *
 * ## 왜 여기서 인가를 다시 묻는가
 *
 * 검색은 이미 인가를 걸어 청크를 돌려줬지만(`DocumentSearchMapper` 의 술어), 원본을 여는 것은
 * 별개의 요청이다. `GET /documents/files/{id}` 가 `canView` 를 다시 판정한다 — <b>이 카드는
 * 권한을 들고 있지 않다.</b> 눌러도 열리지 않을 수 있고, 그때 서버가 준 사유를 그대로 보여 준다.
 *
 * 이 요청은 <b>세션 인증</b>으로 나간다(위임 토큰이 아니다). 쓰기 확인 카드와 같은 규칙이다(§5.4) —
 * 에이전트에게 원본을 여는 통로를 주지 않는다.
 */

export type Citation = {
  documentFileId: string;
  name: string;
  clauseRef?: string | null;
};

/** `GET /documents/files/{id}` 응답 중 이 카드가 쓰는 부분. */
type DocumentFileView = {
  id: string;
  originalName: string;
  /** S3 사본의 presigned GET. 사본이 없거나 저장소가 꺼져 있으면 null. */
  downloadUrl: string | null;
  targets: {
    targetType: 'S3' | 'NAS';
    nasConnectionId: string | null;
    storageKey: string;
    uploadStatus: string;
  }[];
};

/** S3 presign 이 없으면 NAS 사본을 중계 경로로 연다 — NAS 엔 presign 이 없다. */
function resolveHref(file: DocumentFileView): string | null {
  if (file.downloadUrl) return file.downloadUrl;
  const nas = file.targets.find((t) => t.targetType === 'NAS' && t.nasConnectionId && t.uploadStatus === 'DONE');
  return nas?.nasConnectionId ? nasDownloadUrl(nas.nasConnectionId, nas.storageKey) : null;
}

export function CitationCard({ citations }: { citations: Citation[] }) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const open = async (citation: Citation) => {
    setFailure(null);
    setPendingId(citation.documentFileId);

    // 창을 **먼저** 연다. 링크를 받아 온 뒤에 열면 브라우저가 "사용자 동작이 아닌 팝업" 으로 보고
    // 막는다 — 누른 사람에게는 아무 일도 일어나지 않은 것으로 보인다.
    //
    // ★ features 에 `noopener` 를 주면 안 된다. 명세상 그때 `window.open` 은 **null 을 돌려주는데**
    //   탭은 이미 열려 있다 — 핸들이 없으니 닫지도, 그 탭으로 이동시키지도 못한다. 그래서 실패하면
    //   빈 탭이 남고, 성공하면 `window.location.assign` 이 **보고 있던 대화 화면을** 덮어썼다.
    //   "눌렀더니 흰 화면" 이 이것이다(2026-08-05). 핸들을 받은 뒤 `opener` 를 끊는 것으로 같은
    //   보호를 얻는다.
    const opened = window.open('', '_blank');
    if (opened) opened.opener = null;
    try {
      const file = await apiFetch<DocumentFileView>(`/v1/documents/files/${citation.documentFileId}`);
      const href = resolveHref(file);
      if (!href) {
        opened?.close();
        setFailure('원본 파일을 찾지 못했습니다. 자료실에서 직접 확인해 주세요.');
        return;
      }
      if (opened) opened.location.href = href;
      else window.location.assign(href);       // 팝업이 막힌 경우 — 현재 탭으로라도 연다
    } catch (error) {
      opened?.close();
      // 서버가 준 사유를 그대로 보여 준다. 권한 거절("담당 범위가 아닙니다")을 일반 오류로 뭉개면
      // 사용자는 될 일을 안 되는 줄 알고 다시 누른다.
      setFailure(error instanceof ApiRequestError ? error.message : '문서를 열지 못했습니다.');
    } finally {
      setPendingId(null);
    }
  };

  if (citations.length === 0) return null;

  return (
    <div className="max-w-[560px] rounded-2xl bg-white overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      <div className="px-4 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
        <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
          근거 문서 {citations.length}건
        </p>
      </div>

      <ul className="px-2 py-2 space-y-1">
        {citations.map((citation) => (
          <li key={`${citation.documentFileId}:${citation.clauseRef ?? ''}`}>
            <button
              onClick={() => open(citation)}
              disabled={pendingId !== null}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm hover:bg-slate-50 disabled:opacity-50"
              style={{ color: 'var(--text-secondary)' }}
              title="원본 열기"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
              <span className="truncate">{citation.name}</span>
              {citation.clauseRef && (
                <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-xs" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
                  {citation.clauseRef}
                </span>
              )}
              {pendingId === citation.documentFileId && (
                <span className="flex-shrink-0 text-xs" style={{ color: 'var(--text-muted)' }}>여는 중…</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {failure && (
        <div className="px-3 pb-3">
          <Notice tone="warning">{failure}</Notice>
        </div>
      )}
    </div>
  );
}
